import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { getFileIconName } from "@/models/utils/getFileIconName";
import { twMerge } from "@/utils/twMerge";

import type { ChatInputFile, ChatModel } from "@/models/chat/ChatModel";

const File = observer(({ chat, inputFile }: { chat: ChatModel; inputFile: ChatInputFile }) => {
  const t = useTranslations();

  const isUploading = inputFile.status === "uploading";
  const isFailed = inputFile.status === "failed";

  const { tooltip, handlerProps } = useTooltip({
    title: isFailed ? t("chat.uploadFailed") : undefined,
  });

  return (
    <div
      {...handlerProps}
      className={twMerge(
        "group relative flex items-center gap-0.5 rounded-14 h-7 pl-1.5 pr-2.5 max-w-38 shrink-0 overflow-hidden",
        "text-[15px] tracking-tight text-gray-600 dark:text-gray-400",
        "bg-gray-100 dark:bg-gray-900",
        "hover:bg-blue-100 hover:dark:bg-blue-900",
        isFailed && "hover:bg-red-100 hover:dark:bg-red-900",
      )}
    >
      {isUploading && (
        <div
          role="progressbar"
          aria-label={t("chat.uploadingFile", { name: inputFile.file.name })}
          aria-valuenow={Math.round(inputFile.progress * 100)}
          className="absolute inset-y-0 left-0 bg-blue-100 dark:bg-blue-900 transition-[width] duration-200 ease-out"
          style={{ width: `${Math.round(inputFile.progress * 100)}%` }}
        />
      )}
      <Icon
        name={getFileIconName(inputFile.file)}
        width={20}
        height={20}
        className="relative shrink-0 group-hover:[--color-icon-secondary:var(--color-node-active)]"
      />
      <span
        className={twMerge(
          "relative truncate min-w-0 group-hover:text-node-active",
          isFailed && "text-red-500 group-hover:text-red-500",
        )}
      >
        {inputFile.file.name}
      </span>
      {tooltip}
      <button
        type="button"
        aria-label={t("chat.removeFileNamed", { name: inputFile.file.name })}
        // The only focusable part of the chip, so it also tells the upload's state
        aria-description={
          (isFailed && t("chat.uploadFailed")) ||
          (isUploading && t("chat.uploadingFile", { name: inputFile.file.name })) ||
          undefined
        }
        onClick={() => chat.removeInputFile(inputFile.id)}
        className={twMerge(
          "absolute top-1 right-1 flex items-center justify-center size-5 rounded-full",
          "bg-document-bg text-document-text",
          "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity",
          "hover:bg-gray-50 dark:hover:bg-gray-950",
        )}
      >
        <Icon name="CrossSmall" width={16} height={16} />
      </button>
    </div>
  );
});

const ChatInputFiles = observer(function ChatInputFiles({ chat }: { chat: ChatModel }) {
  const files = chat.inputFiles;
  if (files.length === 0) return null;

  return (
    <div className="flex gap-1 min-w-0 overflow-x-auto no-scrollbar">
      {files.map((inputFile) => (
        <File key={inputFile.id} chat={chat} inputFile={inputFile} />
      ))}
    </div>
  );
});

export default ChatInputFiles;
